import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { breadcrumbSchema, jsonLdScript, serviceSchema } from "@/lib/seo";

import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";

const PATH = "/regionen/wuerzburg/seo-agentur";

const services = [
  {
    title: "Technisches SEO",
    body:
      "Crawlbarkeit, Indexierung, Core Web Vitals, Seitenstruktur und Ladezeiten. Die technische Basis entscheidet, ob der Googlebot und KI-Crawler Ihre Inhalte überhaupt sauber erfassen und indexieren — bei vielen Websites liegt hier der größte unentdeckte Hebel für bessere Google-Rankings.",
  },
  {
    title: "OnPage- & Content-Optimierung",
    body:
      "Keyword-Recherche, Search-Intent-Analyse und Content-Strukturen, die zur Such- und Entscheidungssituation Ihrer Zielgruppe passen. Es geht nicht um mehr Content, sondern um den richtigen Content an der richtigen Stelle — eingebunden in Conversion-Pfade, nicht als Selbstzweck. So verstehen Google und KI-Systeme, wofür Ihre Seiten relevant sind.",
  },
  {
    title: "OffPage & Autorität",
    body:
      "Backlink-Analyse und der Aufbau thematischer Autorität. Wir bewerten Linkprofile kritisch und setzen auf nachhaltige Signale statt auf gekaufte Masse, die mittelfristig zum Risiko wird.",
  },
  {
    title: "Local SEO für die Region Würzburg",
    body:
      "Optimierung für lokale Suchanfragen aus Würzburg, Schweinfurt und Mainfranken — Google-Unternehmensprofil, lokale Relevanzsignale und regionale Content-Ausrichtung, wo die Zielgruppe regional sucht.",
  },
  {
    title: "AI-SEO / Generative Engine Optimization",
    body:
      "Optimierung Ihrer Inhalte für die Zitierbarkeit in ChatGPT, Perplexity, Claude und Googles AI Overviews. Ein Feld, das die meisten Würzburger Agenturen noch nicht ernsthaft bedienen — wir tun es seit Beginn der AI-Such-Ära.",
  },
  {
    title: "Wettbewerbs- & Lückenanalyse",
    body:
      "Marktstruktur, Wettbewerber-Sichtbarkeit und thematische Lücken. Wir zeigen, wo Ihre Würzburger Wettbewerber stark sind, wo sie schwach sind und welche Themen wirtschaftlich erreichbar sind.",
  },
];

const differentiators: { lead: string; body: string }[] = [
  {
    lead: "Wirtschaftliche Priorisierung statt Ranking-Vanity",
    body: "Wir messen SEO an Umsatzbeitrag, nicht an Sichtbarkeitsindizes. Was nicht aufs Geschäft einzahlt, machen wir nicht.",
  },
  {
    lead: "Echte AI-SEO-Kompetenz",
    body: "GEO ist für uns kein Buzzword auf der Leistungsseite, sondern gelebte Praxis. Wir publizieren selbst dazu und optimieren Mandate aktiv für KI-Sichtbarkeit.",
  },
  {
    lead: "Steuerung durch den Geschäftsführer",
    body: "Kein Junior-Account-Manager mit 30 Mandaten. Ihre SEO-Strategie verantwortet erfahrene Hand persönlich.",
  },
  {
    lead: "SEO im Gesamtsystem",
    body: "Wir denken SEO nicht isoliert, sondern verzahnt mit SEA und Conversion-Optimierung. SEA zeigt früh, welche Begriffe konvertieren; SEO übernimmt sie nachhaltig und senkt die Abhängigkeit von steigenden Klickpreisen.",
  },
  {
    lead: "Persönliche Termine in Würzburg, wo sinnvoll",
    body: "Die 25 km sind für Strategie-Gespräche und Reviews praktikabel. Die laufende SEO-Arbeit läuft ohnehin digital — wie in der ganzen Branche.",
  },
  {
    lead: "Ehrlichkeit auch beim Nein",
    body: "Wenn SEO in Ihrem Fall nicht der wirtschaftlichste Kanal ist, sagen wir das. Wir verkaufen keine Mandate, die wir nicht seriös vertreten können.",
  },
];

const phases = [
  {
    n: "01",
    title: "Analyse von Sichtbarkeit, Nachfrage und Wirkung",
    body:
      "Wir analysieren nicht nur Rankings, sondern deren tatsächlichen Beitrag zum Geschäft: Welche Suchanfragen kaufbereit sind, welche Seiten zu Umsatz beitragen, wo organischer Traffic konvertiert.",
  },
  {
    n: "02",
    title: "Wirtschaftliche Priorisierung statt SEO-Aktionismus",
    body:
      "Wir priorisieren nach erwartetem Umsatz-Einfluss, Suchintention, Umsetzbarkeit und Skalierbarkeit — kein theoretisch vollständiger, sondern ein wirtschaftlich sinnvoller Fahrplan.",
  },
  {
    n: "03",
    title: "Struktur, Inhalte und Suchintention verzahnen",
    body:
      "Wir optimieren Seiten- und Kategoriestrukturen, Content und interne Verlinkung so, dass sie exakt zur Entscheidungssituation des Nutzers passen — für klassische Suche und für KI-Systeme zugleich.",
  },
  {
    n: "04",
    title: "SEO, SEA und Conversion als ein System",
    body:
      "SEO entfaltet vollen Wert im Zusammenspiel. Iterativer Betrieb mit monatlichem Review auf wirtschaftlichen Kennzahlen statt reiner Ranking-Reports.",
  },
];

const related = [
  {
    label: "SEO & AI SEO",
    body: "Unsere SEO-Methodik im Detail",
    to: "/leistungen/seo-ai-seo",
  },
  {
    label: "Performance Marketing Würzburg",
    body: "Google Ads und Performance Marketing für die Region",
    to: "/regionen/wuerzburg",
  },
  {
    label: "Conversion-Rate-Optimierung",
    body: "Sichtbarkeit braucht Conversion, um zu wirken",
    to: "/leistungen/conversion-rate-optimierung",
  },
  {
    label: "Tracking & Datenbasis",
    body: "Die Datenbasis, ohne die SEO-Wirkung nicht messbar ist",
    to: "/leistungen/tracking-datenbasis",
  },
];

const faqs = [
  {
    q: "Was kostet SEO bei einer Würzburger Agentur?",
    a: "Seriöse SEO lässt sich nicht pauschal bepreisen, weil Aufwand und Hebel von Ausgangslage, Wettbewerb und Zielen abhängen. Wir arbeiten mit transparenten monatlichen Mandaten statt versteckter Stundenpakete und nennen den Rahmen nach der ersten Analyse — bevor Sie sich festlegen.",
  },
  {
    q: "Wie lange dauert es, bis SEO in Würzburg wirkt?",
    a: "SEO ist ein mittelfristiger Kanal. Erste technische und inhaltliche Verbesserungen wirken oft innerhalb von Wochen, der Aufbau nachhaltiger Sichtbarkeit für umkämpfte Begriffe dauert in der Regel mehrere Monate. Wer schnelle Ergebnisse braucht, kombiniert SEO mit Google Ads.",
  },
  {
    q: "Muss eine SEO-Agentur vor Ort in Würzburg sitzen?",
    a: "Für die operative SEO-Arbeit nein — sie läuft praktisch vollständig digital. Wichtiger als die Postleitzahl sind methodische Tiefe und wirtschaftliches Verständnis. Für persönliche Strategie-Termine sind wir mit 25 km Entfernung schnell in Würzburg.",
  },
  {
    q: "Was ist AI-SEO und brauche ich das in Würzburg?",
    a: "AI-SEO (Generative Engine Optimization) optimiert Inhalte dafür, in Antworten von ChatGPT, Perplexity, Claude und Googles AI Overviews zitiert zu werden. Da immer mehr Menschen Fragen direkt an KI-Systeme stellen, wird diese Sichtbarkeit auch für Würzburger Unternehmen zunehmend relevant — unabhängig von der Branche.",
  },
  {
    q: "Welche SEO-Maßnahmen bringen den größten Erfolg?",
    a: "Das hängt von der Ausgangslage ab. Bei vielen Websites liegt der größte Hebel in der technischen Basis und in der Suchintention-gerechten Aufbereitung bestehender Seiten — nicht in immer mehr neuem Content. Wir beginnen mit einer Analyse, die zeigt, welche Maßnahmen in Ihrem konkreten Fall am meisten zu Anfragen und Umsatz beitragen.",
  },
  {
    q: "Wie messen Sie den Erfolg von SEO?",
    a: "Nicht an Ranking-Positionen allein, sondern an deren Beitrag zum Geschäft: Wie viele qualifizierte Besucher und Anfragen entstehen über organische Suche, welche Seiten konvertieren, wie entwickelt sich die Sichtbarkeit in Google und in KI-Systemen. Diese Kennzahlen halten wir in einem monatlichen Review transparent fest.",
  },
  {
    q: "Lohnt sich lokale SEO für Würzburger Unternehmen?",
    a: "Für Unternehmen mit regionaler Zielgruppe ja — lokale Suchanfragen aus Würzburg, Schweinfurt und Mainfranken haben oft hohe Kaufabsicht und geringeren Wettbewerb als überregionale Begriffe. Für überregional tätige B2B-Dienstleister steht dagegen thematische Autorität im Vordergrund. Wir entscheiden anhand Ihres tatsächlichen Marktes, worauf die Strategie zielt.",
  },
  {
    q: "Wie unterscheidet sich Calvarius von anderen SEO-Agenturen in Würzburg?",
    a: "Drei Punkte: Wir priorisieren nach wirtschaftlichem Erfolg statt nach Sichtbarkeits-Kennzahlen, wir verantworten die Strategie auf Geschäftsführer-Ebene statt über wechselnde Junior-Kräfte, und wir optimieren von Anfang an parallel für Google und für KI-Suchsysteme. Wenn SEO für Ihr Geschäft nicht der wirtschaftlichste Kanal ist, sagen wir das auch ehrlich.",
  },
];

function buildHead() {
  const h = pageHead("region:wuerzburg:seo", "de");
  const links = h.links.filter((l) => l.hrefLang !== "en");
  const breadcrumb = breadcrumbSchema([
    { name: "Start", path: "/" },
    { name: "Regionen", path: "/regionen" },
    { name: "Würzburg", path: "/regionen/wuerzburg" },
    { name: "SEO-Agentur", path: PATH },
  ]);
  const service = serviceSchema({
    name: "SEO-Agentur Würzburg",
    description:
      "SEO und AI-SEO für Unternehmen in Würzburg und Unterfranken. Wirtschaftlich priorisiert statt Ranking-Vanity. Klassisches SEO plus Generative Engine Optimization für ChatGPT, Perplexity und Claude.",
    url: PATH,
    serviceType: "Suchmaschinenoptimierung",
    areaServed: ["Würzburg", "Unterfranken", "Mainfranken", "DE"],
  });
  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return {
    meta: h.meta,
    links,
    scripts: [jsonLdScript(breadcrumb), jsonLdScript(service), jsonLdScript(faq)],
  };
}

export const Route = createFileRoute("/regionen/wuerzburg_/seo-agentur")({
  loader: () => ({ alternateLocalePath: "/en/regions/wuerzburg" }),
  head: () => buildHead(),
  component: SeoAgenturWuerzburg,
});

function SeoAgenturWuerzburg() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          number="01"
          eyebrow="Regionen · Würzburg · SEO"
          headline={
            <>
              SEO-Agentur Würzburg —{" "}
              <em>Sichtbarkeit, die wirtschaftlich wirkt.</em>
            </>
          }
          lead="Calvarius ist Ihre SEO-Agentur für Würzburg und Unterfranken — aus Karlstadt am Main, 25 Kilometer nördlich. Wir optimieren nicht für Rankings, sondern für das, was Rankings bringen sollen: qualifizierte Anfragen und messbaren Umsatz. Klassisches SEO bei Google und parallel die Optimierung für ChatGPT, Perplexity und Claude — denn Suche findet zunehmend in KI-Systemen statt, nicht nur in Suchergebnislisten."
          metaStrip={[
            "Im Markt seit 2013",
            "25 km von Würzburg",
            "klassisches SEO + AI-SEO",
          ]}
          primaryCta={{
            label: "Kostenfreien SEO-Check anfragen",
            to: "/kontakt",
            search: { topic: "seo-wuerzburg" },
          }}
          secondaryCta={{ label: "Unsere SEO-Methodik", to: "/leistungen/seo-ai-seo" }}
          breadcrumbs={[
            { label: "Start", to: "/" },
            { label: "Regionen", to: "/regionen" },
            { label: "Würzburg", to: "/regionen/wuerzburg" },
            { label: "SEO-Agentur" },
          ]}
        />

        <Section
          number="02"
          kicker="Standpunkt"
          headline={
            <>
              Was eine SEO-Agentur in Würzburg{" "}
              <em>wirklich leisten muss.</em>
            </>
          }
        >
          <div className="max-w-3xl space-y-6 text-lg leading-relaxed text-ink-2">
            <RevealOnScroll>
              <p>
                Wer in Würzburg eine SEO-Agentur sucht, findet vor allem Versprechen:
                mehr Sichtbarkeit, bessere Rankings, nachhaltiger Traffic. Das klingt
                gut, beantwortet aber nicht die eigentliche Frage. Die lautet: Bringt
                diese Sichtbarkeit meinem Unternehmen tatsächlich Umsatz — oder
                produziert sie nur Statistiken? Genau hier trennt sich solide
                Suchmaschinenoptimierung von teurem Aktionismus.
              </p>
            </RevealOnScroll>
            <RevealOnScroll delay={80}>
              <p>
                Viele Unternehmen investieren über Jahre in SEO und erreichen
                Sichtbarkeit, ohne dass sich diese im Geschäftsergebnis niederschlägt.
                Der Grund ist selten fehlende Arbeit — er ist fast immer falsche
                Priorisierung. SEO bedeutet für uns nicht, möglichst viele Keywords zu
                besetzen, sondern die Suchanfragen mit echter geschäftlicher Relevanz
                zu identifizieren und so zu bedienen, dass daraus messbare Ergebnisse
                entstehen.
              </p>
            </RevealOnScroll>
            <RevealOnScroll delay={160}>
              <h3 className="mt-10 font-display text-2xl leading-snug text-ink">
                Nicht jede Sichtbarkeit ist wertvoll
              </h3>
              <p className="mt-4">
                Ein Ranking auf Position 1 für einen Begriff, den niemand mit
                Kaufabsicht sucht, ist wirtschaftlich wertlos. Wir betrachten SEO nicht
                aus der Ranking-Perspektive, sondern aus der Geschäfts-Perspektive:
                Welche Suchbegriffe sind kauf- oder kontaktbereit, welche Seiten tragen
                messbar zu Umsatz bei, wo konvertiert organischer Traffic. Eine
                Top-Platzierung bei Google ist kein Ziel an sich — sie ist nur dann
                wertvoll, wenn die dahinterstehende Suchanfrage zu Anfragen oder
                Umsatz führt. Daraus entsteht ein SEO-Fahrplan, der nicht theoretisch
                vollständig, sondern wirtschaftlich sinnvoll ist.
              </p>
            </RevealOnScroll>
            <RevealOnScroll delay={220}>
              <h3 className="mt-10 font-display text-2xl leading-snug text-ink">
                SEO und AI-SEO gehören heute zusammen
              </h3>
              <p className="mt-4">
                Klassisches SEO ist nur noch der halbe Weg. Ein wachsender Teil der
                Suchanfragen wird heute von KI-Systemen wie ChatGPT, Perplexity, Claude
                und Googles AI Overviews beantwortet — oft, ohne dass der Nutzer je
                eine klassische Ergebnisliste sieht. Wer in Würzburg langfristig
                gefunden werden will, muss in beiden Welten sichtbar sein. Wir
                optimieren Inhalte parallel für klassische Rankings und für die
                Zitierbarkeit in KI-Antworten (Generative Engine Optimization).
              </p>
            </RevealOnScroll>
          </div>
        </Section>

        <Section
          number="03"
          variant="tinted"
          kicker="Leistungen"
          headline={
            <>
              Was unsere SEO-Agentur für Würzburger Unternehmen{" "}
              <em>konkret tut.</em>
            </>
          }
          caption="Suchmaschinenoptimierung ist kein einzelner Hebel, sondern das Zusammenspiel mehrerer Disziplinen. Welche davon im Vordergrund stehen, hängt von Ihrer Ausgangslage ab — wir priorisieren nach wirtschaftlichem Hebel, nicht nach Vollständigkeit der Checkliste."
        >
          <div className="grid gap-4 md:grid-cols-2">
            {services.map((s, i) => (
              <RevealOnScroll key={s.title} delay={i * 50}>
                <div className="h-full rounded-lg border border-rule bg-white p-7">
                  <h3 className="font-display text-xl leading-snug text-ink">
                    {s.title}
                  </h3>
                  <p className="mt-4 leading-relaxed text-ink-3">{s.body}</p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </Section>

        <Section
          number="04"
          kicker="Differenzierer"
          headline={
            <>
              Warum Calvarius — auch wenn wir{" "}
              <em>nicht in Würzburg sitzen.</em>
            </>
          }
          caption="Ehrlich gesagt: Wenn Sie eine Agentur mit Büro in der Würzburger Innenstadt suchen, gibt es Anbieter, die das bieten. Was Sie bei uns bekommen, ist etwas anderes — und für SEO meist das Wichtigere."
        >
          <ul className="divide-y divide-rule border-t border-b border-rule">
            {differentiators.map((d) => (
              <li
                key={d.lead}
                className="grid gap-4 py-6 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]"
              >
                <div className="font-display text-lg leading-snug text-ink">
                  {d.lead}
                </div>
                <div className="leading-relaxed text-ink-3">{d.body}</div>
              </li>
            ))}
          </ul>
        </Section>

        <Section
          number="05"
          variant="tinted"
          kicker="Methodik"
          headline={
            <>
              Wie wir SEO-Mandate in Würzburg <em>starten.</em>
            </>
          }
          caption="Vier Phasen, die Suchmaschinenoptimierung wirtschaftlich machen."
        >
          <div className="overflow-hidden rounded-lg border border-rule">
            {phases.map((p, i) => (
              <div
                key={p.n}
                className={`grid gap-4 px-6 py-6 md:grid-cols-[64px_minmax(0,1fr)] ${
                  i % 2 === 0 ? "bg-white" : "bg-bg-subtle"
                }`}
              >
                <div className="font-mono text-xs uppercase tracking-wider text-ink-4 md:pt-1">
                  {p.n}
                </div>
                <div>
                  <h3 className="font-display text-lg leading-snug text-ink">
                    {p.title}
                  </h3>
                  <p className="mt-2 leading-relaxed text-ink-3">{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section
          number="06"
          kicker="Regionaler Kontext"
          headline={
            <>
              SEO für den Wirtschaftsraum <em>Würzburg.</em>
            </>
          }
        >
          <div className="max-w-3xl">
            <RevealOnScroll>
              <p className="text-lg leading-relaxed text-ink-2">
                Würzburg verbindet eine Universität mit 35.000 Studierenden mit einem
                etablierten Mittelstand — Handelsbetriebe, B2B-Dienstleister,
                Industrie-Zulieferer und eine wachsende SaaS- und EdTech-Szene. Für
                die Suchmaschinenoptimierung heißt das: Die wirtschaftlich relevanten
                Suchanfragen unterscheiden sich je nach Geschäftsmodell stark. Ein
                regionaler Handelsbetrieb braucht Local SEO und lokale Sichtbarkeit,
                ein überregional tätiger B2B-Dienstleister aus Würzburg braucht
                thematische Autorität für Fach-Suchanfragen in ganz DACH. Wir richten
                die SEO-Strategie an Ihrem tatsächlichen Markt aus — nicht an einer
                pauschalen Würzburg-Schablone.
              </p>
            </RevealOnScroll>
          </div>
        </Section>

        <Section
          number="07"
          variant="tinted"
          kicker="Zusammenarbeit"
          headline={
            <>
              Wie eine SEO-Zusammenarbeit mit{" "}
              <em>Würzburger Unternehmen abläuft.</em>
            </>
          }
        >
          <div className="max-w-3xl space-y-6 text-lg leading-relaxed text-ink-2">
            <RevealOnScroll>
              <p>
                Eine seriöse SEO-Zusammenarbeit beginnt nicht mit Maßnahmen, sondern
                mit Verständnis. Bevor wir an Ihrer Website arbeiten, klären wir,
                welche Ziele Ihr Unternehmen verfolgt, welche Anfragen wirtschaftlich
                wertvoll sind und wo Sie heute im Wettbewerb stehen. Erst aus dieser
                Analyse entsteht ein Plan, dessen Umsetzung sich an messbarem Erfolg
                orientiert — nicht an einer abstrakten Maßnahmen-Liste.
              </p>
            </RevealOnScroll>
            <RevealOnScroll delay={80}>
              <h3 className="mt-10 font-display text-2xl leading-snug text-ink">
                Analyse und klare Ziele zu Beginn
              </h3>
              <p className="mt-4">
                Am Anfang steht eine ehrliche Bestandsaufnahme: Wie viele Besucher
                kommen heute über organische Suche, welche Seiten konvertieren, welche
                Begriffe bringen kaufbereite Nutzer. Daraus definieren wir gemeinsam
                konkrete Ziele — mehr qualifizierte Anfragen, bessere lokale
                Sichtbarkeit in der Region Würzburg, oder Autorität für
                Fach-Suchanfragen in ganz DACH. Ohne klare Ziele ist jede SEO-Maßnahme
                blinder Aktionismus.
              </p>
            </RevealOnScroll>
            <RevealOnScroll delay={160}>
              <h3 className="mt-10 font-display text-2xl leading-snug text-ink">
                Umsetzung mit nachvollziehbaren Schritten
              </h3>
              <p className="mt-4">
                Die Umsetzung priorisieren wir nach wirtschaftlichem Hebel: Was den
                größten Beitrag verspricht, kommt zuerst. Technische Korrekturen,
                Content-Maßnahmen und der Aufbau von Autorität greifen ineinander.
                Jeder Schritt ist dokumentiert und nachvollziehbar — Sie sehen, was
                passiert und warum.
              </p>
            </RevealOnScroll>
            <RevealOnScroll delay={220}>
              <h3 className="mt-10 font-display text-2xl leading-snug text-ink">
                Erfolg, der monatlich messbar ist
              </h3>
              <p className="mt-4">
                Den Erfolg messen wir an wirtschaftlichen Kennzahlen, nicht an
                Sichtbarkeits-Punktzahlen. In einem monatlichen Review zeigen wir,
                wie sich organische Besucher, Anfragen und die Sichtbarkeit in Google
                und KI-Systemen entwickeln — transparent und ohne Reporting-Theater.
                So bleibt die SEO-Strategie an dem ausgerichtet, was zählt: dem
                Beitrag zu Ihrem Geschäft.
              </p>
            </RevealOnScroll>
          </div>
        </Section>

        <Section
          number="08"
          variant="tinted"
          kicker="Verwandte Leistungen"
          headline={<em>Verwandte Leistungen.</em>}
        >
          <div className="grid gap-4 md:grid-cols-2">
            {related.map((r, i) => (
              <RevealOnScroll key={r.to} delay={i * 50}>
                <Link
                  to={r.to}
                  className="group block h-full rounded-lg border border-rule bg-white p-6 transition-colors hover:border-cyan-deep/50"
                >
                  <div className="font-display text-lg leading-snug text-ink">
                    {r.label}
                  </div>
                  <p className="mt-3 leading-relaxed text-ink-3">{r.body}</p>
                  <div className="mt-4 font-mono text-xs uppercase tracking-wider text-cyan-deep group-hover:underline">
                    Mehr erfahren ↗
                  </div>
                </Link>
              </RevealOnScroll>
            ))}
          </div>
        </Section>

        <Section number="09" variant="deep">
          <CTABlock
            variant="deep"
            headline={
              <>
                Wie sichtbar ist Ihre Website <em>heute wirklich?</em>
              </>
            }
            body="Wir sehen uns kostenfrei den SEO-Status Ihrer Website an — klassische Sichtbarkeit und Auffindbarkeit in KI-Systemen — und kommen mit einer ehrlichen ersten Einschätzung zurück: die größten Hebel, ungenutztes Potenzial, blinde Flecken. Kein 80-Seiten-Audit, sondern eine fokussierte Einordnung, die Sie sofort verstehen. Ohne Sales-Pitch, ohne Verpflichtung."
            primary={{
              label: "Kostenfreien SEO-Check anfragen",
              to: "/kontakt",
              search: { topic: "seo-wuerzburg" },
            }}
            secondary={{ label: "Weitere Regionen", to: "/regionen" }}
          />
        </Section>

        <Section
          number="10"
          kicker="FAQ"
          headline={
            <>
              Häufige Fragen zur <em>SEO-Agentur in Würzburg.</em>
            </>
          }
        >
          <div className="divide-y divide-rule border-t border-b border-rule">
            {faqs.map((f) => (
              <div key={f.q} className="py-6">
                <h3 className="font-display text-xl leading-snug text-ink">
                  {f.q}
                </h3>
                <p className="mt-3 leading-relaxed text-ink-3">{f.a}</p>
              </div>
            ))}
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}
